import { FC, useRef, useState } from 'react';
import { BackIcon } from 'assets/icons';
import LineItemPlusReactIcon from 'assets/react-icons/LineItemPlusReactIcon';
import { CustomButton } from 'components';
import AddAttachmentModal from 'components/add-attachment-modal/AddAttachmentsModal';
import { ButtonType } from 'components/custom-button/CustomButton';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { useUploadEstimateImageFileMutation } from '../api';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';
import { isEqual } from 'lodash';

interface AttachmentViewDetailsPanelProps {
  estimateAttachments: any;
  onBackClick: () => void;
  onSave: (attachment: any[]) => void;
  canEdit: boolean;
}

const AttachmentViewDetailsPanel: FC<AttachmentViewDetailsPanelProps> = ({
  estimateAttachments,
  onBackClick,
  onSave,
  canEdit
}) => {
  const [attachments, setAttachments] = useState(estimateAttachments);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState({
    open: false,
    attachment: { id: null, url: null, title: null, imagePreview: null }
  });
  const currentImageUploadCall = useRef(null);
  const currentTeam = useAppSelector(selectCurrentTeam);

  const [uploadFileMutation, { isLoading: isUploadingImage }] = useUploadEstimateImageFileMutation();

  const handleAddPhotoClick = () => {
    setIsAttachmentModalOpen({
      open: true,
      attachment: { id: null, url: null, title: null, imagePreview: null }
    });
  };

  const resetAttachmentModalState = () => {
    setIsAttachmentModalOpen({
      open: false,
      attachment: { id: null, url: null, title: null, imagePreview: null }
    });
  };

  const handleAttachmentModalClose = () => {
    if (isUploadingImage) {
      currentImageUploadCall.current.abort();
    }
    resetAttachmentModalState();
  };

  const handleAttachmentSave = async ({ image, imagePreview, title }) => {
    const { data } = await getImageURL(image);
    const newAttachment = { title, url: data.url, imagePreview, id: null };
    setAttachments(prev => [...prev, newAttachment]);
    resetAttachmentModalState();
  };

  const getImageURL = async imageFile => {
    try {
      const imageUrlFromS3Call = uploadFileMutation({
        file: imageFile,
        teamId: currentTeam.id
      });
      currentImageUploadCall.current = imageUrlFromS3Call;
      const imageUrlFromS3 = await imageUrlFromS3Call.unwrap();
      return imageUrlFromS3;
    } catch (err) {
      // Handle error
    }
  };
  const onRemoveAttachment = attachment => {
    setAttachments(prev => prev.filter(att => att.url !== attachment.url));
  };

  const onSaveClick = async () => {
    const updatedAttachments = attachments.map(({ title, url }) => ({ title, url }));
    onSave(updatedAttachments);
  };

  const areAttachmentsDifferent = () => {
    return !isEqual(attachments, estimateAttachments);
  };

  return (
    <div className="h-full w-96">
      <div className="mb-1 flex items-center border-b border-secondaryBtn py-4">
        <BackIcon className="mx-4 cursor-pointer" id="estimate-details-panel-back-arrow" onClick={onBackClick} />
        <div className="text-17px font-semibold text-headingGray">Attachments</div>
      </div>
      <div className="py-6 px-4">
        {canEdit && (
          <div
            onClick={handleAddPhotoClick}
            className="mb-5 flex w-full cursor-pointer flex-row rounded border border-borderGray py-4 px-5">
            <div className="flex flex-row items-center gap-2">
              <LineItemPlusReactIcon id="create-estimate-add-photo-icon" className="shrink-0" />
              <div id="estimate-add-photo" className="text-17px font-semibold text-secondary">
                Attach another photo
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-5">
          {attachments?.map(attachment => (
            <div
              key={attachment.url}
              className="relative flex w-full flex-row 
            items-center rounded border border-borderGray p-3 last:mb-5">
              <div className="relative mr-4 h-12 w-12 rounded-md">
                <img
                  className="absolute left-1/2 h-full -translate-x-1/2 rounded-md object-cover"
                  src={attachment.urlLink || attachment.imagePreview}
                  alt={attachment.title}
                />
              </div>
              <div className="mb-[6px] break-words text-17px font-semibold text-heading line-clamp-2">
                {attachment.title}
              </div>
              {canEdit && (
                <div className="ml-auto">
                  <FormCloseReactIcon
                    id="create-estimate-remove-attachment-close-icon"
                    onClick={e => {
                      e.stopPropagation();
                      onRemoveAttachment(attachment);
                    }}
                    className="cursor-pointer"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        {isAttachmentModalOpen.open && (
          <AddAttachmentModal
            isEdit={false}
            isUploadingImage={isUploadingImage}
            onClose={handleAttachmentModalClose}
            onSave={handleAttachmentSave}
            attachmentData={isAttachmentModalOpen.attachment}
          />
        )}
      </div>
      <div className="flex w-full flex-col gap-y-4 p-4 pt-0">
        <CustomButton
          id="edit-estimate-tnc-save-button"
          className="w-full"
          onClick={onSaveClick}
          type={ButtonType.PRIMARY}
          disabled={!areAttachmentsDifferent()}>
          Save
        </CustomButton>
        <CustomButton
          id="edit-estimate-tnc-cancel-button"
          className="w-full"
          onClick={onBackClick}
          type={ButtonType.SECONDARY}>
          Cancel
        </CustomButton>
      </div>
    </div>
  );
};

export default AttachmentViewDetailsPanel;
